import { gql } from "@apollo/client";

// ---- Queries ----

export const GET_PROJECTS = gql`
  query GetProjects {
    projects {
      projects {
        id
        name
        description
        statusText
        statusVersion
      }
      activeProject
    }
  }
`;

export const GET_ATLAS_RELEASE_STATUS = gql`
  query GetAtlasReleaseStatus($projectId: ID!) {
    atlasReleaseStatus(projectId: $projectId) {
      id name branch branchSha headState headCheckedAt hilAvailable
      releaseTag releaseSha releaseUrl releasePublishedAt
      build { outcome scope sha runUrl reportUrl counts receivedAt }
      unit { outcome scope sha runUrl reportUrl counts receivedAt }
      hil { outcome scope sha runUrl reportUrl counts receivedAt }
    }
  }
`;

export const GET_USERS = gql`
  query GetUsers {
    users {
      users {
        id
        name
        role
      }
      activeUser
    }
  }
`;

export const GET_REQUIREMENTS = gql`
  query GetRequirements(
    $discipline: String
    $category: String
    $keyword: String
    $tag: String
    $testCoverage: String
    $scope: String
  ) {
    requirements(
      discipline: $discipline
      category: $category
      keyword: $keyword
      tag: $tag
      testCoverage: $testCoverage
      scope: $scope
    ) {
      id
      title
      description
      parentId
      level
      requirementLevel
      completion
      discipline
      category
      owner
      revision
      tags
      depth
      tests {
        id
        description
        type
        suite
        testStatus
        jiraTickets
        links
      }
      requirementDrivers {
        driverType
        id
        label
        active
      }
      sourceDrivers {
        driverType
        id
        label
        active
      }
      drivenChildCount
      activeDriverCount
      correctness
      correctnessIssues {
        code
        message
      }
    }
  }
`;

export const GET_REQUIREMENT = gql`
  query GetRequirement($id: String!) {
    requirement(id: $id) {
      id
      title
      description
      level
      requirementLevel
      completion
      discipline
      category
      owner
      revision
      tags
      depth
      status
      canEdit
      editableFields
      activeUser
      baseRequirementId
      projectVariants
      sources
      requirementDrivers {
        driverType
        id
        label
        active
      }
      sourceDrivers {
        driverType
        id
        label
        active
      }
      drivenRequirements
      activeDriverCount
      requirementDriverCount
      sourceDriverCount
      drivenChildCount
      testCount
      isActive
      correctness
      correctnessIssues {
        code
        message
      }
      tests {
        id
        description
        type
        suite
        jiraTickets
        links
      }
    }
  }
`;

export const GET_TESTS = gql`
  query GetTests(
    $keyword: String
    $type: String
    $suite: String
    $status: String
    $issues: String
  ) {
    tests(
      keyword: $keyword
      type: $type
      suite: $suite
      status: $status
      issues: $issues
    ) {
      id
      title
      description
      type
      suite
      jiraTickets
      jiraStatus
      allDone
      hasIssues
      issues
      requirements
      relatedTests
      tags
      sources
      testStatus
    }
  }
`;

export const GET_TEST = gql`
  query GetTest($id: String!) {
    test(id: $id) {
      id
      title
      description
      type
      suite
      jiraTickets
      jiraStatus
      allDone
      hasIssues
      issues
      requirements
      relatedTests
      linkedRequirements
      tags
      sources
      testStatus
    }
  }
`;

export const GET_STATS = gql`
  query GetStats {
    stats {
      project
      projectId
      projectName
      snapshotDate
      total
      parentCount
      childCount
      disciplines
      categories
      revisions
      statuses
      completionCounts
      testCount
      testTypes
      testSuites
      depthCounts
      levelCounts
      levels
      tags
      childrenWithTests
      childrenWithoutTests
      owners
      activeRequirementCount
      inactiveRequirementCount
      correctRequirementCount
      incorrectRequirementCount
      missingActiveDriversCount
      missingImplementationTestsCount
      groupingWithTestsCount
      groupingWithSourceDriversCount
      systemWithoutSourceDriversCount
      systemWithoutDrivenChildrenCount
    }
  }
`;

export const GET_METRICS = gql`
  query GetMetrics {
      metrics {
      translation
      translationTarget
      totalCustomerReqs
      customerReqsWithProduct
      incorporation
      incorporationTarget
      completion
      completionTarget
      automation
      automationTarget
      totalProductReqs
      productReqsWithTests
      reqsCompleted
      reqsAutomated
      disciplines
    }
  }
`;

export const GET_REPORTING_AVAILABILITY = gql`
  query GetReportingAvailability($projectId: ID!) {
    reportingAvailability(projectId: $projectId) {
      timezone
      defaultReportDate
      earliestReportDate
      latestReportDate
      historyReady
      message
    }
  }
`;

export const GET_METRIC_REPORT = gql`
  query GetMetricReport($id: ID!) {
    metricReport(id: $id) {
      id
      projectId
      projectName
      projectStatus
      releaseVersions { skuName version updatedOn }
      mode
      reportDate
      timezone
      capturedAt
      generatedAt
      warnings
      metrics { key label actual total ratio target }
      disciplines {
        discipline
        metrics { key label actual total ratio target }
      }
    }
  }
`;

export const GET_GANTT = gql`
  query GetGantt {
    gantt {
      rows {
        reqId
        description
        discipline
        status
        completion
        hasTests
        allTestsDone
        start
        end
        tests {
          testId
          type
          suite
          status
          done
          start
          end
          jiraTickets
        }
      }
      issues {
        type
        severity
        id
        reqId
        message
      }
      totalRows
      withTimeline
      allDone
      issueCount
    }
  }
`;

// ---- Mutations ----

export const ACTIVATE_PROJECT = gql`
  mutation ActivateProject($projectId: String!) {
    activateProject(projectId: $projectId) {
      activeProject
    }
  }
`;

export const ACTIVATE_USER = gql`
  mutation ActivateUser($userId: String!) {
    activateUser(userId: $userId) {
      activeUser
    }
  }
`;

export const UPDATE_PROJECT_STATUS = gql`
  mutation UpdateProjectStatus($projectId: String!, $statusText: String!, $expectedVersion: Int!) {
    updateProjectStatus(projectId: $projectId, statusText: $statusText, expectedVersion: $expectedVersion) {
      id
      name
      description
      statusText
      statusVersion
    }
  }
`;

export const CREATE_METRIC_REPORT = gql`
  mutation CreateMetricReport($projectId: ID!, $mode: MetricReportMode!, $reportDate: Date, $requestId: String!) {
    createMetricReport(projectId: $projectId, mode: $mode, reportDate: $reportDate, requestId: $requestId) {
      status
      message
      report {
        id
        projectId
        projectName
        projectStatus
        releaseVersions { skuName version updatedOn }
        mode
        reportDate
        timezone
        capturedAt
        generatedAt
        warnings
        metrics { key label actual total ratio target }
        disciplines {
          discipline
          metrics { key label actual total ratio target }
        }
      }
    }
  }
`;

export const UPDATE_METRIC_TARGETS = gql`
  mutation UpdateMetricTargets(
    $translation: Float
    $incorporation: Float
    $completion: Float
    $automation: Float
  ) {
    updateMetricTargets(
      translation: $translation
      incorporation: $incorporation
      completion: $completion
      automation: $automation
    ) {
      translation
      translationTarget
      incorporation
      incorporationTarget
      completion
      completionTarget
      automation
      automationTarget
      totalCustomerReqs
      customerReqsWithProduct
      totalProductReqs
      productReqsWithTests
      reqsCompleted
      reqsAutomated
      disciplines
    }
  }
`;

export const UPDATE_REQUIREMENT = gql`
  mutation UpdateRequirement($id: String!, $input: RequirementInput!) {
    updateRequirement(id: $id, input: $input) {
      id
      changed
      completion
      depth
      level
    }
  }
`;

export const LINK_REQUIREMENT_DRIVER = gql`
  mutation LinkRequirementDriver($requirementId: String!, $driverRequirementId: String!) {
    linkRequirementDriver(requirementId: $requirementId, driverRequirementId: $driverRequirementId) {
      success
      message
    }
  }
`;

export const UNLINK_REQUIREMENT_DRIVER = gql`
  mutation UnlinkRequirementDriver($requirementId: String!, $driverRequirementId: String!) {
    unlinkRequirementDriver(requirementId: $requirementId, driverRequirementId: $driverRequirementId) {
      success
      message
    }
  }
`;

export const CREATE_REQUIREMENT = gql`
  mutation CreateRequirement($input: CreateRequirementInput!) {
    createRequirement(input: $input) {
      id
      description
      level
      completion
    }
  }
`;

export const GET_JIRA_STATUS = gql`
  query GetJiraStatus {
    jiraStatus {
      configured
      baseUrl
      mappedTests
      cachedTickets
      schedulingEnabled
      schedulingControlAvailable
      status
      cycleId
      mode
      processed
      updated
      warnings
      lastError
      lastAttemptAt
      lastSuccessAt
      lastFullSuccessAt
      nextDueAt
    }
  }
`;

export const SET_JIRA_AUTOMATIC_SYNC = gql`
  mutation SetJiraAutomaticSync($enabled: Boolean!) {
    setJiraAutomaticSync(enabled: $enabled) {
      schedulingEnabled
      schedulingControlAvailable
      status
      lastAttemptAt
      lastSuccessAt
    }
  }
`;

export const GET_AUDIT_LOG = gql`
  query GetAuditLog($user: String, $fromTs: String, $toTs: String, $limit: Int) {
    auditLog(user: $user, fromTs: $fromTs, toTs: $toTs, limit: $limit) {
      id
      ts
      userId
      userRole
      activeProject
      method
      path
      requestBody
      responseStatus
      responseBody
      durationMs
    }
  }
`;

export const GET_RUNTIME_LOGS = gql`
  query GetRuntimeLogs($limit: Int) {
    runtimeLogs(limit: $limit) {
      id
      ts
      level
      logger
      message
    }
  }
`;

export const SYNC_JIRA = gql`
  mutation SyncJira {
    syncJira {
      status
      cycleId
      synced
      errors
      totalKeys
      warnings
      processed
      updated
      progress
    }
  }
`;

export const GET_USER_ACTIONS = gql`
  query GetUserActions($user: String, $fromTs: String, $toTs: String, $limit: Int) {
    userActions(user: $user, fromTs: $fromTs, toTs: $toTs, limit: $limit) {
      id
      ts
      userId
      userRole
      projectId
      action
      entityType
      entityId
      summary
      details
    }
  }
`;

export const GET_ENTITY_HISTORY = gql`
  query GetEntityHistory($entityType: String!, $entityId: String!, $limit: Int) {
    entityHistory(entityType: $entityType, entityId: $entityId, limit: $limit) {
      id
      ts
      userId
      entityType
      entityId
      action
      snapshot
      changedFields
    }
  }
`;

export const DELETE_REQUIREMENT = gql`
  mutation DeleteRequirement($id: String!, $projectId: String!, $userId: String!) {
    deleteRequirement(id: $id, projectId: $projectId, userId: $userId)
  }
`;

export const DELETE_TEST = gql`
  mutation DeleteTest($id: String!, $userId: String!) {
    deleteTest(id: $id, userId: $userId)
  }
`;

export const CREATE_TEST = gql`
  mutation CreateTest($input: CreateTestInput!, $projectId: String!, $userId: String!) {
    createTest(input: $input, projectId: $projectId, userId: $userId) {
      id
      type
      suite
      description
    }
  }
`;

export const LINK_TEST_TO_REQUIREMENT = gql`
  mutation LinkTestToRequirement($testId: String!, $requirementId: String!, $projectId: String!, $userId: String!) {
    linkTestToRequirement(testId: $testId, requirementId: $requirementId, projectId: $projectId, userId: $userId) {
      success
      message
    }
  }
`;

export const UPDATE_TEST = gql`
  mutation UpdateTest($id: String!, $input: UpdateTestInput!, $userId: String!) {
    updateTest(id: $id, input: $input, userId: $userId) {
      id
      changed
    }
  }
`;

export const GET_SOURCES = gql`
  query GetSources {
    sources {
      id
      label
      url
    }
  }
`;

export const GET_SOURCE_USAGE = gql`
  query GetSourceUsage($sourceId: String!) {
    sourceUsage(sourceId: $sourceId) {
      requirements {
        id
        title
        description
      }
      tests {
        id
        title
        description
      }
    }
  }
`;

export const CREATE_SOURCE = gql`
  mutation CreateSource($input: SourceInput!) {
    createSource(input: $input) {
      id
      label
      url
    }
  }
`;

export const UPDATE_SOURCE = gql`
  mutation UpdateSource($id: String!, $label: String, $url: String) {
    updateSource(id: $id, label: $label, url: $url) {
      id
      label
      url
    }
  }
`;

export const DELETE_SOURCE = gql`
  mutation DeleteSource($id: String!) {
    deleteSource(id: $id)
  }
`;

export const LINK_SOURCE_TO_REQUIREMENT = gql`
  mutation LinkSourceToRequirement($sourceId: String!, $requirementId: String!) {
    linkSourceToRequirement(sourceId: $sourceId, requirementId: $requirementId) {
      success
      message
    }
  }
`;

export const UNLINK_SOURCE_FROM_REQUIREMENT = gql`
  mutation UnlinkSourceFromRequirement($sourceId: String!, $requirementId: String!) {
    unlinkSourceFromRequirement(sourceId: $sourceId, requirementId: $requirementId) {
      success
      message
    }
  }
`;

export const LINK_SOURCE_TO_TEST = gql`
  mutation LinkSourceToTest($sourceId: String!, $testId: String!) {
    linkSourceToTest(sourceId: $sourceId, testId: $testId) {
      success
      message
    }
  }
`;

export const UNLINK_SOURCE_FROM_TEST = gql`
  mutation UnlinkSourceFromTest($sourceId: String!, $testId: String!) {
    unlinkSourceFromTest(sourceId: $sourceId, testId: $testId) {
      success
      message
    }
  }
`;
